import lodash from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import styled from 'styled-components';
import { ampli } from '../../../../../../../ampli';
import { SpinnerLoader } from '../../../../../../../components/common/loading/SpinnerLoader';
import {
  IProductFilter,
  IProductFilterOption,
  productFiltersConstant,
} from '../../../../../../../constants/product/productFilter.constant';
import { useGetProductsQuery } from '../../../../../../../hooks/api/product/queries/useGetProducts.query';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/store/useStore';
import { productFilterSlice } from '../../../../../../../store/productFilterSlice';
import { changeProductFilterOptionIsSelected } from '../../../../../../../utils/filter/changeProductFilterOptionIsSelected.util';
import { createProductFilterSearchParams } from '../../../../../../../utils/filter/createProductFilterSearchParams.util';
import { resetProductFilterOptionIsSelected } from '../../../../../../../utils/filter/resetProductFilterOptionIsSelected.util';
import { ProductFilterInput } from './components/ProductFilterInput';

interface ProductFilterControllerProps {
  closeModal: () => void;
}

export const ProductFilterController = ({ closeModal }: ProductFilterControllerProps) => {
  const dispatch = useAppDispatch();
  const productFilterStates = useAppSelector((state) => state.productFilter);
  const productFilterActions = productFilterSlice.actions;

  // 전역 상태로 적용된 필터 값을 깊은 복사로 가져오기 위한 함수
  const getGlobalProductFilters = () => {
    return lodash.cloneDeep(productFilterStates.globalProductFilters);
  };

  // 전역 상태로 적용된 필터 값을 지역 상태로 가져와 사용
  const [localProductFilters, setLocalProductFilters] = useState<IProductFilter[]>(getGlobalProductFilters());

  useEffect(() => {
    // 지역 상태로 관리하는 필터의 업데이트 조건이 충족되면 업데이트 수행
    if (productFilterStates.haveToRefreshLocalStates) {
      setLocalProductFilters(getGlobalProductFilters());
      dispatch(productFilterActions.changeHaveToRefreshLocalStates(false));
    }
  }, [productFilterStates.haveToRefreshLocalStates]);

  const { data: preProducts, isLoading: isGetPreProductsQueryLoading } = useGetProductsQuery({
    searchParam: createProductFilterSearchParams({ filters: localProductFilters }),
  });

  // 각 필터 영역의 ref, 필터 추가 시 직접 추가 필요
  const filterRef0 = useRef<HTMLDivElement | null>(null);
  const filterRef1 = useRef<HTMLDivElement | null>(null);
  const filterRef2 = useRef<HTMLDivElement | null>(null);
  const filterRef3 = useRef<HTMLDivElement | null>(null);

  // 자신(filter)에게 적용된 ref 를 가져올 때 사용
  const getFilterRef = (filterIndex: number) => {
    switch (filterIndex) {
      case 0:
        return filterRef0;
      case 1:
        return filterRef1;
      case 2:
        return filterRef2;
      default:
        return filterRef3;
    }
  };

  // filterRef 들이 나열된 SelectSection 의 Ref, 각 filterRef 의 Header 로 scroll 할 때 사용
  const filterSelectSectionRef = useRef<HTMLDivElement | null>(null);

  // SelectSectionRef 의 scrollY 값을 클릭한 메뉴 버튼의 ref header 로 옮기기 위한 함수
  const scrollToClickedFilter = (filterIndex: number) =>
    filterSelectSectionRef.current?.scrollTo({
      top: getFilterRef(filterIndex).current?.offsetTop,
    });

  // SelectSection의 scrollY 값을 관리하는 지역 상태
  const [filterSelectSectionScrollTop, setFilterSelectSectionScrollTop] = useState<number>(0);

  // SelectSection의 scrollY 값을 height 값으로 변경할 때 사용하는 함수
  const changeFilterSelectSectionScrollTop = (height: number) => {
    setFilterSelectSectionScrollTop(height);
  };

  useEffect(() => {
    // SelectSection에서 scroll 이 발생할 때, scrollTop 값을 지역 상태로 등록하기 위한 코드
    filterSelectSectionRef.current?.addEventListener('scroll', () =>
      changeFilterSelectSectionScrollTop(Number(filterSelectSectionRef.current?.scrollTop)),
    );
  }, []);

  useEffect(() => {
    if (typeof productFilterStates.clickedFilterButtonIndex === 'number') {
      // 슬라이드 모달이 화면 하단에 들어가 있는 상태에서 offsetTop 값이 0 이 되는 현상이 있음
      // 따라서 슬라이드 모달가 올라오면서 offsetTop 제대로 걸린 이후에 스크롤을 수행하도록 setTimeout 설정함
      setTimeout(() => {
        scrollToClickedFilter(productFilterStates.clickedFilterButtonIndex as number);
      }, 200);

      dispatch(productFilterActions.changeClickedFilterButtonIndex(undefined));
    }
  }, [productFilterStates.clickedFilterButtonIndex]);

  // 현재 SelectSectionRef에서의 scroll 위치와 필터 별 Ref 위치를 비교하여, 각 필터 메뉴의 활성화 여부를 관리하는 코드
  // (1개의 필터 메뉴만 활성화될 수 있음)
  const isFilterMenuActived = useMemo<boolean[]>(() => {
    const filterMenuActived = [false, false, false, false];

    const PIXELS_FOR_ACTIVE_MENU = 30;

    const filterRef1Height = Number(filterRef1.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef2Height = Number(filterRef2.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef3Height = Number(filterRef3.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;

    // 네 번째 필터 메뉴 활성화 여부 계산
    // (마지막 필터 메뉴 요소부터 역으로 계산해야 함
    // 각 필터의 height 값이 500, 1000, 1500 이고 scrollY 값이 1100 인 경우,
    // 1100 > 500 으로 첫 번째 메뉴 버튼이 활성화 됨.
    // 따라서 가장 밑에 있는 필터 메뉴부터 height 값을 계산함)
    if (filterSelectSectionScrollTop >= filterRef3Height) {
      filterMenuActived[3] = true;
      return filterMenuActived;
    }

    // 세 번째 필터 메뉴 활성화 여부 계산
    if (filterSelectSectionScrollTop >= filterRef2Height) {
      filterMenuActived[2] = true;
      return filterMenuActived;
    }

    // 두 번째 필터 메뉴 활성화 여부 계산
    if (filterSelectSectionScrollTop >= filterRef1Height) {
      filterMenuActived[1] = true;
      return filterMenuActived;
    }

    // 위 조건문에 모두 걸리지 않는 경우, 기본으로 첫번째 필터 메뉴를 활성화 함
    filterMenuActived[0] = true;
    return filterMenuActived;
  }, [filterSelectSectionScrollTop]);

  // 필터 Input (radio / checkbox) 클릭 시 해당 input 의 type 에 맞게 지역 상태가 갖고 있는 필터 옵션의 isSelected 값을 변경함
  const handleFilterInputClick = (params: { filter: IProductFilter; option: IProductFilterOption }) => {
    ampli.pvmClickProductFilterOptionInput({
      filter_key: params.filter.key,
      filter_option_name: params.option.optionName,
    });
    ReactGA.event('pvm_click_product_filter_option_input', {
      filter_key: params.filter.key,
      filter_option_name: params.option.optionName,
    });

    const newProductFilters = changeProductFilterOptionIsSelected({
      currentFilters: localProductFilters,
      targetFilter: params.filter,
      targetOption: params.option,
    });

    setLocalProductFilters(newProductFilters);
  };

  // 초기화 버튼 클릭 시 지역 상태가 갖고 있는 필터 옵션의 isSelected 값을 초기화함 (전역 상태와 동일하게 X, 아예 처음으로 필터 값으로 되돌림)
  const handleResetLocalFiltersButtonClick = () => {
    setLocalProductFilters(() => {
      const newProductFilters = resetProductFilterOptionIsSelected({ currentFilters: localProductFilters });
      return newProductFilters;
    });
  };

  // n대 보기(== 적용하기) 버튼 클릭 시 지역 상태의 값을 전역 상태로 반영하고, 모달을 닫음 => 필터 적용
  const handleApplyFilterButtonClick = () => {
    dispatch(productFilterActions.changeFilters(localProductFilters));
    dispatch(productFilterActions.changeHaveToRefreshLocalStates(true));
    closeModal();
  };

  return (
    <Container>
      <FilterMenuSection>
        {productFiltersConstant.map((filter, index) => (
          <FilterMenuButton isActive={isFilterMenuActived[index]} onClick={() => scrollToClickedFilter(index)}>
            <FilterMenuButtonText>{filter.title}</FilterMenuButtonText>
          </FilterMenuButton>
        ))}
      </FilterMenuSection>

      <FilterSelectSection ref={filterSelectSectionRef}>
        {localProductFilters.map((filter, index) => (
          <FilterWrap ref={getFilterRef(index)} isFirst={index === 0} isLast={index === localProductFilters.length - 1}>
            <FilterTitleWrap>
              <FilterTitle>{filter.title}</FilterTitle>
            </FilterTitleWrap>

            {filter.options.map((option) => (
              <ProductFilterInput
                filter={filter}
                option={option}
                handleClick={() => handleFilterInputClick({ filter, option })}
                localProductFilters={localProductFilters}
              />
            ))}
          </FilterWrap>
        ))}
      </FilterSelectSection>

      <ButtonContainer>
        <BlurSection />

        <GreyButton onClick={handleResetLocalFiltersButtonClick}>
          <GreyButtonText>{'초기화'}</GreyButtonText>
        </GreyButton>
        <BlueButton
          disabled={preProducts?.length === 0 || isGetPreProductsQueryLoading}
          onClick={handleApplyFilterButtonClick}
        >
          {!isGetPreProductsQueryLoading && (
            <BlueButtonText>{preProducts?.length === 0 ? '매물 없음' : `${preProducts?.length}대 보기`}</BlueButtonText>
          )}
          <SpinnerLoader isVisible={isGetPreProductsQueryLoading} />
        </BlueButton>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  height: 700px;

  display: flex;
  align-items: flex-start;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  position: relative;
`;

const FilterMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const FilterMenuButton = styled.button<{ isActive: boolean }>`
  width: 80px;
  height: 48px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;
  gap: 10px;

  border-radius: var(--radius-none, 0px);
  background: ${(props) => props.isActive && '#FFF'};
`;

const FilterMenuButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */

  white-space: nowrap;
`;

const FilterSelectSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);

  overflow-y: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const FilterWrap = styled.div<{ isFirst: boolean; isLast: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  padding-top: ${(props) => !props.isFirst && '32px'};
  padding-bottom: ${(props) => (props.isLast ? '520px' : '32px')};

  border-bottom: ${(props) => !props.isLast && '1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed)'};
`;

const FilterTitleWrap = styled.div`
  height: 48px;

  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 0px 24px;
  gap: 10px;
`;

const FilterTitle = styled.span`
  flex: 1 0 0;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const FilterOptionWrap = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: var(--radius-large, 16px) 24px var(--radius-large, 16px) 40px;

  border-radius: var(--radius-medium, 8px);
`;

const BlurSection = styled.div`
  position: absolute;
  left: 0px;
  top: -40px;

  width: 100%;
  max-width: 512px;
  height: 40px;

  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);
`;

const ButtonContainer = styled.div`
  position: fixed;

  left: 0;
  bottom: 0;

  width: 100%;

  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 0px 24px 32px 24px;
  gap: 10px;

  background-color: white;
`;

const GreyButton = styled.button`
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  padding: 16px;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const GreyButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const BlueButton = styled.button`
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  padding: 16px;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);

  &:disabled {
    opacity: 0.3;
  }
`;

const BlueButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
